// QRContentStep.jsx - Updated to use Context
import React from 'react';
import styled from '@emotion/styled';
import { ArrowRight, Download } from 'lucide-react';
import { useQRContext } from '../context/QRContext';
import QRContentTypeSelector from './QRContentTypeSelector';

const StepContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 2rem;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    padding: 1.75rem;
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const StepTitle = styled.h2`
  color: white;
  font-size: clamp(1.75rem, 5vw, 2.5rem);
  font-weight: 700;
  margin: 0 0 0.5rem;
  line-height: 1.2;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: clamp(1rem, 1.25vw, 1.8rem);
  margin: 0 0 1.5rem;
  line-height: 1.5;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
  }
`;

const FormSection = styled.div`
  flex: 1;
  width: 100%;
  margin-bottom: 1.5rem;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 0.5rem;
  font-size: clamp(1rem, 1.5vw, 1.8rem);
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.875rem 1rem;
  color: white;
  font-size: 1rem;
  min-height: 48px;
  box-sizing: border-box;
  -webkit-appearance: none;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
    min-height: 44px;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.875rem 1rem;
  color: white;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  box-sizing: border-box;
  -webkit-appearance: none;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1rem;
    min-height: 100px;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  margin-top: auto;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ButtonsGroup = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PrimaryButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(124, 58, 237, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: white;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    box-shadow:
      0 8px 20px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QRContentStep = () => {
  const {
    contentType,
    formData,
    setContentType,
    setUrl,
    setText,
    setContactField,
    nextStep,
    canProceed,
  } = useQRContext();

  const handleUrlChange = (e) => {
    setUrl(e.target.value);
  };

  const handleTextChange = (e) => {
    setText(e.target.value);
  };

  const handleContactChange = (field) => (e) => {
    setContactField(field, e.target.value);
  };

  return (
    <StepContent>
      <StepTitle>What would you like to create?</StepTitle>
      <StepDescription>
        Choose the type of QR code and enter your content. Your QR code will
        generate automatically as you type.
      </StepDescription>

      <FormSection>
        <QRContentTypeSelector
          contentType={contentType}
          setContentType={setContentType}
        />

        {contentType === 'url' && (
          <InputGroup>
            <Label>Website URL</Label>
            <Input
              type="url"
              placeholder="example.com"
              value={formData.url}
              onChange={handleUrlChange}
            />
          </InputGroup>
        )}

        {contentType === 'text' && (
          <InputGroup>
            <Label>Your Text</Label>
            <TextArea
              placeholder="Enter any text you'd like to encode..."
              value={formData.text}
              onChange={handleTextChange}
            />
          </InputGroup>
        )}

        {contentType === 'contact' && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                marginBottom: '1.5rem',
              }}
            >
              <InputGroup>
                <Label>First Name</Label>
                <Input
                  type="text"
                  placeholder="John"
                  value={formData.contact.firstName}
                  onChange={handleContactChange('firstName')}
                />
              </InputGroup>
              <InputGroup>
                <Label>Last Name</Label>
                <Input
                  type="text"
                  placeholder="Doe"
                  value={formData.contact.lastName}
                  onChange={handleContactChange('lastName')}
                />
              </InputGroup>
            </div>
            <InputGroup>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="john@example.com"
                value={formData.contact.email}
                onChange={handleContactChange('email')}
              />
            </InputGroup>
            <InputGroup>
              <Label>Phone</Label>
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={formData.contact.phone}
                onChange={handleContactChange('phone')}
              />
            </InputGroup>
          </>
        )}
      </FormSection>

      <NavigationButtons>
        <SecondaryButton disabled={!canProceed()}>
          <Download size={16} />
          Download Now
        </SecondaryButton>
        <ButtonsGroup>
          <PrimaryButton onClick={nextStep} disabled={!canProceed()}>
            Customize
            <ArrowRight size={16} />
          </PrimaryButton>
        </ButtonsGroup>
      </NavigationButtons>
    </StepContent>
  );
};

export default QRContentStep;
